import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, OnInit, output, signal, Signal, Type } from '@angular/core';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { IMDMenuOption, MDContentBoxComponent, MDHeaderComponent, MDIconComponent, MDTagComponent } from '@melodic-ui/components';
import { IEnrollmentDetailItem } from '../../api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { IUserSummary } from '../../api/mylb/interfaces/iuser-summary.interface';
import { GradeStateService } from '@lb/state/grade-state.service';
import { GradingTemplateStateService } from '@lb/state/grading-template-state.service';
import { UserStateService } from '@lb/state/user-state.service';
import { RouterModule } from '@angular/router';
import { NoInstructorComponent } from '../no-instructor/no-instructor.component';
import { format } from 'date-fns';
import { buildLessonTimeSlots, ILessonTimeSlot } from '../lessons/utils/lesson-time-slots.util';
import { GlobalTimezoneService } from '../../services/global-timezone.service';
import { EnrollmentStatus } from '../../api/mylb/enums/enrollment-status.enum';
import { ProgramCompletionSummaryComponent } from '../program-completion-summary/program-completion-summary.component';
import { CopyToClipboardComponent } from '../copy-to-clipboard/copy-to-clipboard.component';
import { EnrollmentTagComponent } from '../enrollment-tag/enrollment-tag.component';
import { ProgramTypeTagComponent } from '../program-type-tag/program-type-tag.component';
import { GradeTagComponent } from '../grade-tag/grade-tag.component';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';
import { TimezoneStateService } from '@lb/state/timezone-state.service';
import { AccountStateService } from '@lb/state/account-state.service';

@Component({
	selector: 'lb-enrollment-card-v2',
	standalone: true,
	imports: [
		MDContentBoxComponent,
		UserIconComponent,
		MDIconComponent,
		MDHeaderComponent,
		CommonModule,
		RouterModule,
		MDTagComponent,
		NoInstructorComponent,
		ProgramCompletionSummaryComponent,
		CopyToClipboardComponent,
		EnrollmentTagComponent,
		ProgramTypeTagComponent,
		GradeTagComponent
	],
	templateUrl: './enrollment-card-v2.component.html',
	styleUrl: './enrollment-card-v2.component.scss'
})
export class EnrollmentCardV2Component implements OnInit {
	private _timezoneStateService = inject(TimezoneStateService);
	private _accountStateService = inject(AccountStateService);
	private _userStateService = inject(UserStateService);
	private gradeState = inject(GradeStateService);
	private templateState = inject(GradingTemplateStateService);
	private _globalTimezoneService = inject(GlobalTimezoneService);
	private _appConfig = inject(APP_CONFIG);
	private _availableTimezones = this._timezoneStateService.timezones;

	enrollment = input.required<IEnrollmentDetailItem>();

	enableLessonDetails = computed(() => usesLessonDetailsV2(this.enrollment(), this._appConfig));
	userView = input.required<'learner' | 'instructor'>();
	isReadOnly = input<boolean>(false);
	showScheduleAction = input<boolean>(false);

	enrollmentGradeDialog = input<Type<Component> | undefined>();
	extraLessonDialog = input<Type<Component> | undefined>();
	enrollmentChangeDialog = input<Type<Component> | undefined>();

	enrollmentAction = output<string>();
	viewDetails = output<number>();
	scheduleRequested = output<number>();

	private adminUrl = signal<string>('');

	public canViewNotes = computed(() => this._accountStateService.isAdmin || this._accountStateService.isInstructor);
	public isUserAdmin = this._accountStateService.isAdmin;
	public isUserInstructor = this._accountStateService.isInstructor;

	// User display data is owned by User.Api (never the enrollment model) — resolved from cached summaries.
	learner: Signal<IUserSummary | null> = computed(() => this._userStateService.getUserSummary(this.enrollment().learnerUserID)());
	/** The learner's public tags (type IsAdminOnly = 0), off the same cached summary as the name. Under 13 today. */
	learnerTags = computed(() => this.learner()?.tags ?? []);
	instructor: Signal<IUserSummary | null> = computed(() => {
		const instructorID = this.enrollment().instructorUserID;
		return instructorID ? this._userStateService.getUserSummary(instructorID)() : null;
	});

	isAwaitingSchedule = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.AwaitingSchedule);
	isAwaitingMatch = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.AwaitingMatch);
	isInProgress = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.InProgress);
	isReadyToStart = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.ReadyToStart);

	isMIA = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.MIA);
	isOnLeaveOfAbsence = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.OnLeaveOfAbsence);

	private readonly _schedulePendingStatuses = new Set([EnrollmentStatus.AwaitingSchedule, EnrollmentStatus.AwaitingMatch, EnrollmentStatus.ReadyToStart]);
	isInProgressOrLater = computed(() => !this._schedulePendingStatuses.has(this.enrollment().enrollmentStatusID ?? 0));
	isPaused = computed(() => this.enrollment().enrollmentStatusID === EnrollmentStatus.Paused);

	// Field show/hide kept in lockstep with EnrollmentCardComponent (the v1 card).
	showStartDate = computed(() => !this.isMIA() && !this.isOnLeaveOfAbsence());
	showLastLessonDate = computed(() => !this.isAwaitingSchedule() && !this.isAwaitingMatch() && !this.isMIA() && !this.isOnLeaveOfAbsence());
	showMustStartBy = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch() || this.isReadyToStart());
	showEnrollmentExpiration = computed(() => this.isInProgressOrLater());
	showLessonFrequency = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch());
	isScheduleCTA = computed(() => this.isAwaitingSchedule() && this.showScheduleAction() && !this.isUserInstructor());
	languageName = computed(() => this.enrollment().languageName);
	programName = computed(() => this.enrollment().programName);
	relatedInstitution = computed(() => this.enrollment().institutionName);
	detailsExpanded = computed(() => this._detailsExpanded());

	startDateToBeConfirmed = computed(() => this.isAwaitingSchedule() || this.isAwaitingMatch());

	private _detailsExpanded = signal<boolean>(true);

	toggleDetails(): void {
		this._detailsExpanded.update((expanded) => !expanded);
	}

	learningNeeds = computed(() => this.enrollment().notes?.filter((x) => x.noteTypeID == 2)[0]);
	languageExperience = computed(() => this.enrollment().notes?.filter((x) => x.noteTypeID == 1)[0]);

	/** Program completion summary is computed server-side and carried on the detail item. */
	programSummary = computed(() => this.enrollment().programSummary);

	/**
	 * Submitted semester final grades for display, one per graded semester. Empty unless the enrollment
	 * has a grading template and at least one resolvable final grade. The effective grade is the override
	 * when present, otherwise the calculated grade; names are resolved from the grade scale (User-agnostic).
	 */
	finalGrades = computed(() => {
		if (!this.enrollment().gradingTemplateID || !this.enrollment().isGraded) {
			return [];
		}

		const template = this.templateState.getTemplateSnapshot(this.enrollment().gradingTemplateID);
		const gradeScale = template ? this.gradeState.getGradeScaleByID(template.gradeScaleID) : null;
		const grades = gradeScale?.grades ?? [];
		return (this.enrollment().semesters ?? [])
			.map((s) => {
				const fg = s.semesterFinalGrade;
				if (!fg) {
					return null;
				}
				const gradeID = fg.isOverriden ? fg.overridenGradeID : fg.overallCalculatedGradeID;
				const gradeName = grades.find((g) => g.gradeID === gradeID)?.gradeName ?? null;
				return gradeName ? { semesterID: s.semesterID, levelName: s.levelName, gradeName } : null;
			})
			.filter((g): g is { semesterID: number; levelName: string; gradeName: string } => g !== null);
	});

	startDateLabel = computed(() => {
		if (this.isAwaitingSchedule() || this.isAwaitingMatch()) {
			return 'Selected Date';
		}

		return 'First Lesson Date';
	});

	private _startDateRaw = computed(() => {
		const dateVal = this.enrollment().startDate;
		return dateVal ? new Date(dateVal) : new Date();
	});

	startDate = computed(() => format(this._startDateRaw(), 'd MMM yyyy'));

	endDateLabel = computed(() => 'Last Lesson Date');

	endDate = computed(() => {
		const dateVal = this.enrollment().endDate;
		const date = dateVal ? new Date(dateVal) : new Date();
		return format(date, 'd MMM yyyy');
	});

	timeZoneName: Signal<string> = computed(() => {
		const timeZones = this._availableTimezones();
		const timezoneID = this.learner()?.timezoneID;
		return timeZones.find((tz) => tz.timeZoneID === timezoneID)?.timeZoneName ?? '';
	});

	timeSlotSelections: Signal<ILessonTimeSlot[]> = computed(() =>
		buildLessonTimeSlots({
			schedules: this.enrollment().enrollmentSchedules ?? [],
			availableTimezones: this._availableTimezones(),
			selectedTimezone: this._globalTimezoneService.selectedTimezone(),
			fallbackTimezoneID: this.learner()?.timezoneID
		})
	);

	menuOptions = computed<IMDMenuOption[]>(() => [
		// Not-graded enrollments (chirp rooms) have no semester grades to show.
		...(this.enrollment().isGraded
			? [
					{
						label: "Semester's grades",
						click: () => this.enrollmentAction.emit('semester-grades')
					}
				]
			: []),
		{
			label: 'Propose extra lessons',
			click: () => this.enrollmentAction.emit('extra-lessons')
		},
		{
			label: 'Suggest other changes',
			click: () => this.enrollmentAction.emit('suggest-changes')
		}
	]);

	constructor() {
		this.gradeState.loadGradeScales();

		// User summaries are globally cached and request-deduped, so each card loading its own is cheap.
		effect(() => {
			const enrollment = this.enrollment();
			const userIDs = [enrollment.learnerUserID, enrollment.instructorUserID].filter((id): id is number => id != null);
			if (userIDs.length) {
				this._userStateService.loadUserSummaries(userIDs);
			}

			// Lazy (D3): first card with a template triggers the one fetch-all; template-less
			// enrollments (no grade UI) never do.
			this.templateState.loadGradingTemplates([enrollment.gradingTemplateID]);
		});
	}

	ngOnInit(): void {
		fetch('assets/app-settings.json')
			.then((response) => response.json())
			.then(async (config: IAppSettings) => {
				if (config.adminAppUrl == null) {
					console.error('Admin app URL is not defined in the configuration.');
				}

				this.adminUrl.set(config.adminAppUrl ?? '');
			});
	}

	onCardClick(): void {
		if (this.isScheduleCTA()) {
			this.scheduleRequested.emit(this.enrollment().enrollmentID);
		} else {
			this.viewDetails.emit(this.enrollment().enrollmentID);
		}
	}

	openLink(type: 'instructor' | 'enrollment' | 'learner'): void {
		if (!this.isUserAdmin()) {
			return;
		}

		switch (type) {
			case 'instructor':
				window.open(`${this.adminUrl()}/instructor/${this.enrollment().instructorUserID}/basic-information`, '_blank');
				break;
			case 'enrollment':
				window.open(`${this.adminUrl()}/enrollments/${this.enrollment().enrollmentID}`, '_blank');
				break;
			case 'learner':
				window.open(`${this.adminUrl()}/learner/${this.enrollment().learnerUserID}/basic-information`, '_blank');
				break;
			default:
				console.error('Invalid type provided for openLink:', type);
				break;
		}
	}
}
